import { config } from "../../config";

// Pure helpers for the dashboard's Device Type / Appliance dropdowns.

const DEVICE_TYPES = ["energiSync", "thermoSmarts", "weatherSensors", "environSyncs", "doorSensors"];

// A room's devices of every type, each tagged with its type, in the order the
// Appliance dropdown lists them.
export const roomDevices = (room) =>
  DEVICE_TYPES.flatMap((type) =>
    (room?.[type] ?? []).map((device) => ({ ...device, type: config.deviceType[type] }))
  );

// The rooms the Area dropdown offers: those with at least one device.
export const roomsWithDevices = (rooms = []) =>
  rooms.filter((room) => roomDevices(room).length > 0);

// ------------------------------------------------------------ Device Type

// The Device Type dropdown's entry for devices with no type set yet.
export const NO_TYPE = "__no_type__";
export const NO_TYPE_LABEL = "No type set";

// A device's type as Device Master sets it (the asset's category), tidied:
// "  boh   ac " -> "boh ac". "" when it has none.
export const deviceTypeOf = (device) =>
  String(device?.category ?? device?.deviceType ?? "").trim().replace(/\s+/g, " ");

const byName = (a, b) =>
  (a.name || a.deviceId).localeCompare(b.name || b.deviceId, undefined, { numeric: true });

// Every device in the store, once, tagged with its type and the name of the
// area it sits in.
export const storeDevices = (rooms = []) => {
  const seen = new Set();
  const devices = [];
  for (const room of rooms) {
    for (const device of roomDevices(room)) {
      if (!device.deviceId || seen.has(device.deviceId)) continue;
      seen.add(device.deviceId);
      devices.push({ ...device, roomName: room.roomName ?? "" });
    }
  }
  return devices;
};

// The Device Type dropdown's options: one per type in the store, with its
// devices by name. Types differing only in case are one; the first spelling
// met names it. Sorted by name, "No type set" last.
export const deviceTypeGroups = (rooms = []) => {
  const groups = new Map();
  for (const device of storeDevices(rooms)) {
    const type = deviceTypeOf(device);
    const key = type ? type.toLowerCase() : NO_TYPE;
    if (!groups.has(key)) groups.set(key, { key, label: type || NO_TYPE_LABEL, devices: [] });
    groups.get(key).devices.push(device);
  }
  const list = [...groups.values()];
  for (const group of list) group.devices.sort(byName);
  return list.sort((a, b) => {
    if (a.key === NO_TYPE || b.key === NO_TYPE) return a.key === NO_TYPE ? 1 : -1;
    return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
  });
};

// The group holding `deviceId` and that device, or null - for a deep link,
// which names a device but no type.
export const groupOfDevice = (groups, deviceId) => {
  for (const group of groups) {
    const device = group.devices.find((candidate) => candidate.deviceId === deviceId);
    if (device) return { group, device };
  }
  return null;
};

// The type and appliance picked earlier, if both are still in `groups`, so
// coming back to the dashboard keeps them instead of jumping to the first of
// each. null when either has gone (another store was loaded, the device's type
// was changed, or the saved pick is from before types replaced areas), in
// which case the caller falls back to the defaults.
//
// The device is returned from the freshly built list rather than as saved, so
// the dropdowns get the same object they list.
export const keptSelection = (groups, { deviceType, selectedDeviceDetails } = {}) => {
  const deviceId = selectedDeviceDetails?.deviceId;
  if (!deviceType || !deviceId) return null;
  const group = groups.find((candidate) => candidate.key === deviceType);
  const device = group?.devices.find((candidate) => candidate.deviceId === deviceId);
  return device ? { group, device } : null;
};
